// Full-bleed sunset landmark scene used behind the landing-page hero.
// Lifted from the redesign handoff (originally the "How it works" header).
// Palette is intentionally fixed (warm sunset) rather than theme-driven —
// it's a photographic-style backdrop, not a UI surface.
export function HeroScene() {
  return (
    <svg viewBox="0 0 1600 600" preserveAspectRatio="xMidYMid slice" className="how-scene-svg">
      <defs>
        <linearGradient id="hlSky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#F4B488" />
          <stop offset="60%" stopColor="#E88A5F" />
          <stop offset="100%" stopColor="#A8401F" />
        </linearGradient>
      </defs>
      <rect width="1600" height="600" fill="url(#hlSky)" />
      <circle cx="900" cy="160" r="80" fill="#FFE4B5" opacity="0.9" />
      <circle cx="900" cy="160" r="130" fill="#FFE4B5" opacity="0.15" />
      <rect y="500" width="1600" height="100" fill="#7A2D13" opacity="0.4" />
      <path d="M 0 500 L 1600 500" stroke="#7A2D13" strokeWidth="2" opacity="0.5" />

      {/* Eiffel Tower (Paris) */}
      <g transform="translate(180 280)">
        <text x="50" y="-10" textAnchor="middle" fontSize="11" fontFamily="ui-monospace,monospace" fill="#FFF" opacity="0.85">PARIS</text>
        <path d="M 50 0 L 30 100 L 25 220 L 10 220 L 12 230 L 88 230 L 90 220 L 75 220 L 70 100 Z" fill="#3E1F11" />
        <rect x="25" y="80" width="50" height="4" fill="#3E1F11" />
        <rect x="20" y="130" width="60" height="4" fill="#3E1F11" />
        <rect x="15" y="180" width="70" height="4" fill="#3E1F11" />
      </g>

      {/* Tokyo Tower */}
      <g transform="translate(440 270)">
        <text x="50" y="-10" textAnchor="middle" fontSize="11" fontFamily="ui-monospace,monospace" fill="#FFF" opacity="0.85">TOKYO</text>
        <path d="M 50 0 L 40 60 L 25 230 L 10 230 L 12 240 L 88 240 L 90 230 L 75 230 L 60 60 Z" fill="#5A1F0E" />
        <rect x="40" y="40" width="20" height="20" fill="#5A1F0E" />
        <circle cx="50" cy="10" r="3" fill="#FF0000">
          <animate attributeName="opacity" values="1;0.3;1" dur="1.4s" repeatCount="indefinite" />
        </circle>
      </g>

      {/* Big Ben (London) */}
      <g transform="translate(680 200)">
        <text x="50" y="-10" textAnchor="middle" fontSize="11" fontFamily="ui-monospace,monospace" fill="#FFF" opacity="0.85">LONDON</text>
        <rect x="35" y="0" width="30" height="60" fill="#3E1F11" />
        <polygon points="30,60 70,60 50,30" fill="#3E1F11" />
        <rect x="30" y="60" width="40" height="240" fill="#5A1F0E" />
        <circle cx="50" cy="100" r="14" fill="#FFE4B5" />
        <line x1="50" y1="100" x2="50" y2="92" stroke="#3E1F11" strokeWidth="1.5" />
        <line x1="50" y1="100" x2="56" y2="100" stroke="#3E1F11" strokeWidth="1.5" />
      </g>

      {/* Statue of Liberty (NYC) */}
      <g transform="translate(940 240)">
        <text x="40" y="-10" textAnchor="middle" fontSize="11" fontFamily="ui-monospace,monospace" fill="#FFF" opacity="0.85">NEW YORK</text>
        <rect x="25" y="200" width="30" height="60" fill="#3E1F11" />
        <rect x="35" y="80" width="10" height="120" fill="#5A1F0E" />
        <circle cx="40" cy="70" r="14" fill="#5A1F0E" />
        <polygon points="40,40 28,72 52,72" fill="#5A1F0E" />
        <line x1="40" y1="40" x2="40" y2="20" stroke="#5A1F0E" strokeWidth="2" />
        <circle cx="40" cy="18" r="3" fill="#FFE4B5" />
      </g>

      {/* Sydney Opera House */}
      <g transform="translate(1180 350)">
        <text x="80" y="-10" textAnchor="middle" fontSize="11" fontFamily="ui-monospace,monospace" fill="#FFF" opacity="0.85">SYDNEY</text>
        <path d="M 0 150 Q 40 30 80 150 Z" fill="#5A1F0E" />
        <path d="M 50 150 Q 100 50 150 150 Z" fill="#3E1F11" />
        <path d="M 100 150 Q 140 60 180 150 Z" fill="#5A1F0E" />
      </g>

      {/* Burj Khalifa (Dubai) */}
      <g transform="translate(1400 280)">
        <text x="40" y="-10" textAnchor="middle" fontSize="11" fontFamily="ui-monospace,monospace" fill="#FFF" opacity="0.85">DUBAI</text>
        <polygon points="20,220 60,220 56,180 24,180" fill="#3E1F11" />
        <polygon points="24,180 56,180 53,140 27,140" fill="#5A1F0E" />
        <polygon points="27,140 53,140 50,100 30,100" fill="#3E1F11" />
        <polygon points="30,100 50,100 47,70 33,70" fill="#5A1F0E" />
        <polygon points="33,70 47,70 45,40 35,40" fill="#3E1F11" />
        <polygon points="35,40 45,40 43,18 37,18" fill="#5A1F0E" />
        <line x1="40" y1="18" x2="40" y2="-4" stroke="#3E1F11" strokeWidth="2.4" />
        <circle cx="40" cy="-4" r="2.5" fill="#3E1F11" />
        <rect x="34" y="50" width="3" height="6" fill="#FFE9C8" opacity="0.5" />
        <rect x="32" y="115" width="3" height="6" fill="#FFE9C8" opacity="0.5" />
        <rect x="29" y="195" width="3" height="6" fill="#FFE9C8" opacity="0.5" />
      </g>

      {/* Connection arcs between landmarks */}
      <g fill="none" stroke="#FFFFFF" strokeWidth="2" strokeDasharray="5 6" opacity="0.65">
        <path d="M 230 250 Q 360 100 490 240">
          <animate attributeName="stroke-dashoffset" from="0" to="-22" dur="1.2s" repeatCount="indefinite" />
        </path>
        <path d="M 490 240 Q 620 80 730 170">
          <animate attributeName="stroke-dashoffset" from="0" to="-22" dur="1.4s" repeatCount="indefinite" />
        </path>
        <path d="M 730 170 Q 870 60 980 210">
          <animate attributeName="stroke-dashoffset" from="0" to="-22" dur="1.3s" repeatCount="indefinite" />
        </path>
        <path d="M 980 210 Q 1150 100 1280 320">
          <animate attributeName="stroke-dashoffset" from="0" to="-22" dur="1.5s" repeatCount="indefinite" />
        </path>
        <path d="M 1280 320 Q 1380 200 1440 290">
          <animate attributeName="stroke-dashoffset" from="0" to="-22" dur="1.1s" repeatCount="indefinite" />
        </path>
      </g>

      {/* Pulse dots at each landmark */}
      {[
        { x: 230, y: 250 },
        { x: 490, y: 240 },
        { x: 730, y: 170 },
        { x: 980, y: 210 },
        { x: 1280, y: 320 },
        { x: 1440, y: 290 },
      ].map((p, i) => (
        <g key={i}>
          <circle cx={p.x} cy={p.y} r="6" fill="#FFFFFF" />
          <circle cx={p.x} cy={p.y} r="14" fill="none" stroke="#FFFFFF" strokeWidth="2">
            <animate attributeName="r" values="6;20;6" dur="2s" begin={`${i * 0.25}s`} repeatCount="indefinite" />
            <animate attributeName="opacity" values="1;0;1" dur="2s" begin={`${i * 0.25}s`} repeatCount="indefinite" />
          </circle>
        </g>
      ))}
    </svg>
  )
}
